import React from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { AnimatePresence, motion } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, Plus } from 'lucide-react';
import CustomAnalysisCard from '@/components/customAnalysis/CustomAnalysisCard';
import CustomAnalysisSorter from '@/components/customAnalysis/CustomAnalysisSorter';
import NewAnalysisTypeModal from '@/components/customAnalysis/NewAnalysisTypeModal';
import { useCustomTypes } from '@/hooks/useCustomTypes';

const CustomAnalysis = () => {
    const { user } = useAuth();
    const {
        sortedTypes: customTypes,
        loading,
        error,
        deleteLoading,
        isModalOpen,
        sortBy,
        handleDelete,
        handleModalClose,
        setIsModalOpen,
        setSortBy
    } = useCustomTypes(user?.id);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin" />
            </div>
        );
    }

    return (
        <div className="p-6 space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold">Custom Analysis Types</h1>
                    <p className="hidden lg:inline-block text-gray-500 mt-1">
                        Manage your custom website analysis configurations
                    </p>
                </div>
                <Button
                    className="flex items-center bg-blue-600 hover:bg-blue-700 gap-2"
                    onClick={() => setIsModalOpen(true)}
                >
                    <Plus className="w-4 h-4" />
                    Create New
                </Button>
            </div>

            {error && (
                <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-lg" role="alert">
                    <p className="text-sm font-medium">{error}</p>
                </div>
            )}

            {customTypes?.length > 0 && (
                <div className="flex">
                    <CustomAnalysisSorter value={sortBy} onValueChange={setSortBy} />
                </div>
            )}

            {!customTypes?.length ? (
                <Card className="p-12">
                    <div className="text-center">
                        <h3 className="text-lg font-semibold">No custom analysis types yet</h3>
                        <p className="text-gray-500 mt-2">
                            Create your first custom analysis type to get started
                        </p>
                        <Button
                            className="mt-4"
                            onClick={() => setIsModalOpen(true)}
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Create Your First Type
                        </Button>
                    </div>
                </Card>
            ) : (
                <motion.div
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                    layout
                >
                    <AnimatePresence mode="popLayout">
                        {customTypes.map((type) => (
                            <CustomAnalysisCard
                                key={type.id}
                                type={type}
                                onDelete={handleDelete}
                                deleteLoading={deleteLoading === type.id}
                            />
                        ))}
                    </AnimatePresence>
                </motion.div>
            )}

            <NewAnalysisTypeModal
                isModalOpen={isModalOpen}
                setIsModalOpen={handleModalClose}
                fetchAnalysisTypes={handleModalClose}
            />
        </div>
    );
};

export default CustomAnalysis;